import { useState, useEffect, useRef } from 'react';
import { Fab, Badge, Zoom, useTheme, useMediaQuery } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { environment } from '../../../environments/environment';

/**
 * FloatingCartButton - A floating action button that appears when navbar scrolls out of view
 *
 * Z-Index Hierarchy:
 * - MUI Dialog: 1300 (FattyPattyDialog)
 * - MUI Drawer: 1200 (CartDrawer)
 * - FloatingCartButton: 1100 (Below both dialogs and drawer)
 * - Page Content: < 1000
 *
 * Behavior:
 * - Only shows when header is scrolled out of view (using IntersectionObserver)
 * - Hidden when CartDrawer is open
 * - Hidden when cart is empty
 * - Moves up (100px from bottom) when FattyPattyDialog is open on mobile
 */
const FloatingCartButton = ({ onOpenCart, isCartDrawerOpen = false }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const [showFloatingCart, setShowFloatingCart] = useState(false);
  const [isFattyPattyDialogOpen, setIsFattyPattyDialogOpen] = useState(false);
  const headerRef = useRef(null);
  const hoverTimerRef = useRef(null);

  const cart = useSelector((state) => state.shopReducer.cart);
  const cartItemCount = Array.isArray(cart)
    ? cart.reduce(
        (total, item) => total + (parseInt(item?.quantity, 10) || 1),
        0
      )
    : 0;

  // Detect if FattyPattyDialog is open by checking for its presence in the DOM
  useEffect(() => {
    const checkDialogOpen = () => {
      const dialog = document.querySelector('[role="dialog"]');
      setIsFattyPattyDialogOpen(!!dialog);
    };

    checkDialogOpen();

    const observer = new MutationObserver(checkDialogOpen);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const header =
      document.querySelector('header') ||
      document.querySelector('[role="banner"]') ||
      document.querySelector('nav');

    if (!header) {
      setShowFloatingCart(false);
      return;
    }

    headerRef.current = header;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowFloatingCart(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '0px' }
    );

    observer.observe(header);

    return () => {
      if (headerRef.current) observer.unobserve(headerRef.current);
      observer.disconnect();
    };
  }, []);

  const handleMouseEnter = () => {
    hoverTimerRef.current = setTimeout(() => {
      onOpenCart();
    }, 400);
  };

  const handleMouseLeave = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  const shouldShow = showFloatingCart && cartItemCount > 0 && !isCartDrawerOpen;
  const bottomPosition =
    smDown && isFattyPattyDialogOpen ? 100 : smDown ? 16 : 24;

  return (
    <Zoom in={shouldShow} unmountOnExit>
      <Fab
        color="primary"
        aria-label="shopping cart"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => navigate('/cart')}
        sx={{
          position: 'fixed',
          bottom: bottomPosition,
          right: smDown ? 16 : 24,
          zIndex: 2000,
          backgroundColor: environment.mainColor,
          color: 'white',
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: environment.mainColor,
            transform: 'scale(1.1)',
            boxShadow: '0px 12px 32px rgba(0, 0, 0, 0.2)',
          },
          '&:active': {
            transform: 'scale(0.95)',
          },
        }}
      >
        <Badge
          badgeContent={cartItemCount}
          color="error"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: '#ff4444',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '12px',
              minWidth: '22px',
              height: '22px',
              borderRadius: '11px',
              border: '2px solid white',
            },
          }}
        >
          <ShoppingCartIcon sx={{ fontSize: 28 }} />
        </Badge>
      </Fab>
    </Zoom>
  );
};

export default FloatingCartButton;
